import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'classnames';

import './style.scss';

const MainMenuItems = memo(({ onClick = () => {} }) => {
  const intl = useIntl();
  const { pathname } = useLocation();

  return (
    <div className="c-main-menu__nav" id="main-menu-nav" role="menu" aria-label={intl.formatMessage({ id: 'components.header.menu.aria-label', defaultMessage: 'Main navigation' })}>
      <div className="c-vertical-nav c-context-menu__nav">

        <Link
            className={cx('c-vertical-nav__item', {
            'c-vertical-nav__item--active': matchPath(`/`, { path: pathname, exact: true }),
            })}
            to={`/`}
            rel="nofollow"
            onClick={() => onClick(false)}
            aria-current={matchPath(`/`, { path: pathname, exact: true }) ? 'page' : undefined}
            role="menuitem"
        >
          {intl.formatMessage({
            id: 'components.header.menu.home-page',
            defaultMessage: 'Home Page',
          })}
        </Link>

        <Link
            className={cx('c-vertical-nav__item', {
            'c-vertical-nav__item--active': matchPath(`/intake/client`, { path: pathname, exact: true }),
            })}
            to={`/intake/client`}
            rel="nofollow"
            onClick={() => onClick(false)}
            aria-current={matchPath(`/intake/client`, { path: pathname, exact: true }) ? 'page' : undefined}
            role="menuitem"
        >
          {intl.formatMessage({
            id: 'components.header.menu.client-intake',
            defaultMessage: 'Find your help',
          })}
        </Link>

        <FormattedMessage id="routes.cleaning-work">
          {(msg) => (
            <Link
              className={cx('c-vertical-nav__item', {
                'c-vertical-nav__item--active': matchPath(`/intake/worker`, { path: pathname, exact: true }),
              })}
              to={`/intake/worker`}
              rel="nofollow"
              onClick={() => onClick(false)}
              aria-current={matchPath(`/intake/worker`, { path: pathname, exact: true }) ? 'page' : undefined}
              role="menuitem"
            >
              {intl.formatMessage({
                id: 'components.header.menu.cleaning-work',
                defaultMessage: 'Cleaning work',
              })}
            </Link>
          )}
        </FormattedMessage>

        <FormattedMessage id="routes.faq">
          {(msg) => (
            <Link
              className={cx('c-vertical-nav__item', {
                'c-vertical-nav__item--active': matchPath(`/${msg}`, { path: pathname, exact: true }),
              })}
              to={`/${msg}`}
              onClick={() => onClick(false)}
              aria-current={matchPath(`/${msg}`, { path: pathname, exact: true }) ? 'page' : undefined}
              role="menuitem"
            >
              {intl.formatMessage({
                id: 'components.header.menu.faq',
                defaultMessage: 'FAQ',
              })}
            </Link>
          )}
        </FormattedMessage>

        <FormattedMessage id="routes.contact">
          {(msg) => (
            <Link
              className={cx('c-vertical-nav__item', {
                'c-vertical-nav__item--active': matchPath(`/${msg}`, { path: pathname, exact: true }),
              })}
              to={`/${msg}`}
              onClick={() => onClick(false)}
              aria-current={matchPath(`/${msg}`, { path: pathname, exact: true }) ? 'page' : undefined}
              role="menuitem"
            >
              {intl.formatMessage({
                id: 'components.header.menu.contact',
                defaultMessage: 'Contact',
              })}
            </Link>
          )}
        </FormattedMessage>

        <FormattedMessage id="routes.privacy-policy">
          {(msg) => (
            <Link
              className={cx('c-vertical-nav__item', {
                'c-vertical-nav__item--active': matchPath(`/${msg}`, { path: pathname, exact: true }),
              })}
              to={`/${msg}`}
              onClick={() => onClick(false)}
              aria-current={matchPath(`/${msg}`, { path: pathname, exact: true }) ? 'page' : undefined}
              role="menuitem"
            >
              {intl.formatMessage({
                id: 'components.header.menu.privacy-policy',
                defaultMessage: 'Privacy policy',
              })}
            </Link>
          )}
        </FormattedMessage>

        <FormattedMessage id="routes.terms-and-conditions">
          {(msg) => (
            <Link
              className={cx('c-vertical-nav__item', {
                'c-vertical-nav__item--active': matchPath(`/${msg}`, { path: pathname, exact: true }),
              })}
              to={`/${msg}`}
              onClick={() => onClick(false)}
              aria-current={matchPath(`/${msg}`, { path: pathname, exact: true }) ? 'page' : undefined}
              role="menuitem"
            >
              {intl.formatMessage({
                id: 'components.footer.copyright.terms-and-conditions',
                defaultMessage: 'Terms and conditions',
              })}
            </Link>
          )}
        </FormattedMessage>
      </div>
    </div>
  );
});

const MainMenu = () => {
  const [visible, setVisible] = useState(false);
  const toggleRef = useRef(null);

  const handleVisible = useCallback((isVisible) => {
    if (isVisible) {
      document.body.classList.add('l-noscroll');
    } else {
      document.body.classList.remove('l-noscroll');
      toggleRef.current?.focus();
    }
    setVisible(isVisible);
  });

  const handleOverlayClick = useCallback(() => {
    if (!visible) {
      return;
    }

    document.body.classList.remove('l-noscroll');
    setVisible(false);
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && visible) {
        handleVisible(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, handleVisible]);

  useEffect(() => {
    if (visible) {
      const menu = document.getElementById('main-menu-nav');
      const firstItem = menu?.querySelector('[role="menuitem"]');
      firstItem?.focus();
    }
  }, [visible]);

  return (
    <div className={cx('c-horizontal-nav__item c-header__navitem c-main-menu', { 'c-main-menu--opened': visible })}>
      <div className="c-overlay" onClick={handleOverlayClick} role="presentation" />
      <button
        type="button"
        ref={toggleRef}
        id="main-menu-ham-button"
        aria-label="Main menu"
        aria-haspopup="menu"
        aria-expanded={visible}
        aria-controls="main-menu-nav"
        className="c-main-menu__toggle hamburger-lines"
        onClick={() => handleVisible(!visible)}
      >
        <span className="line line1" aria-hidden="true" />
        <span className="line line2" aria-hidden="true" />
        <span className="line line3" aria-hidden="true" />
      </button>
      <MainMenuItems onClick={handleVisible} />
    </div>
  );
};

export default MainMenu;
